import Sidebar from '../../components/Sidebar';
import { Header } from '../../components/header';
import { DeductionINvoiceSection } from '../../components/deduction-invoice-section.component';
import DeductionFilters from '../../components/deduction-filters.component';
import DeductionTabs from '../../components/deduction-tabs.component';
import AddDeductionButton from '../../components/add-deduction-button.component';
import NotInvoicedSection from '../../components/not-invoiced-section.component';
import AddDeductionModal from '../../components/add-deduction-modal.component';
import { useTranslation } from 'react-i18next';
import { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../hooks/useRedux';
import { fetchInvoiceEditData } from '../../features/customer/customer.slice';
import { fetchTimesheetDeductions } from '../../features/timesheet/timesheet.slice';
import { useForm } from 'react-hook-form';
import { useDeductionPageSubmit } from '../../hooks/useDeductionPageSubmit.hook';
import './deduction.page.scss';

function DeductionPage() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('invoiced');
    
    // Get userId and customers from Redux
    const userId = useSelector((state: any) => state.auth?.user?.userId);

    // Clear filters when switching tabs
    useEffect(() => {
        setSelectedYear('all');
        setSelectedMonth('all');
        setSelectedClient('all');
        setSearchQuery('');
    }, [activeTab]);
    const { customers, loading: customersLoading } = useSelector((state: any) => state.customer || { customers: [], loading: false });
    const globalData = useSelector((state: any) => state?.globalData?.data);
    
    // Filter states
    const [selectedYear, setSelectedYear] = useState('all');
    const [selectedMonth, setSelectedMonth] = useState('all');
    const [selectedClient, setSelectedClient] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    
    // Modal state management
    const [showDeductionModal, setShowDeductionModal] = useState(false);
    const [editingDeductionData, setEditingDeductionData] = useState<any | null>(null);
    const [selectedCustomerForDeduction, setSelectedCustomerForDeduction] = useState<number | null>(null);
    
    // React Hook Form setup
    const {
        register,
        setValue,
        watch,
        formState: { errors },
        trigger,
        reset
    } = useForm();
    
    // Default preferences for deduction modal
    const preferences = useMemo(() => ({
        vat: 25,
        currency: 'SEK'
    }), []);
    
    // Use deduction submit hook
    const { handleDeductionSubmit } = useDeductionPageSubmit({
        editingDeductionData,
        selectedCustomerId: selectedCustomerForDeduction,
        preferences,
        globalData,
        userId: userId || 0
    });
    
    // Fetch customers on component mount
    useEffect(() => {
        if (userId) {
            dispatch(fetchInvoiceEditData(userId));
        }
    }, [userId, dispatch]);
    
    // Convert customers to dropdown options
    const customerOptions = useMemo(() => {
        return customers.map((customer: any) => ({
            value: String(customer.customerId || customer.id),
            label: customer.name || customer.customerName
        }));
    }, [customers]);

    // Tabs configuration
    const tabs = useMemo(() => [
        { key: 'invoiced', label: t('INVOICED') || 'Invoiced' },
        { key: 'not-invoiced', label: t('NOT_INVOICED') || 'Not Invoiced' }
    ], [t]);

    // Modal handlers
    const handleOpenModal = () => {
        setEditingDeductionData(null);
        setSelectedCustomerForDeduction(null);
        reset(); // Reset form
        setShowDeductionModal(true);
    };

    const handleOpenEditModal = (deductionData: any) => {
        setEditingDeductionData(deductionData);
        setSelectedCustomerForDeduction(deductionData.customerId || null);
        setShowDeductionModal(true);
    };

    const handleCloseModal = () => {
        setShowDeductionModal(false);
        setEditingDeductionData(null);
        setSelectedCustomerForDeduction(null);
        reset(); // Reset form
    };

    // Wrapper: on success → close modal + re-fetch the not-invoiced list
    const handleModalSubmit = async (deductionData: any) => {
        await handleDeductionSubmit(deductionData);
        // Success — close modal then refresh the list
        handleCloseModal();

        // Convert year and month to DateTime format for refetch
        let yearDateTime = null;
        let monthDateTime = null;
        
        if (selectedYear && selectedYear !== 'all') {
            const yearNum = parseInt(selectedYear);
            const monthNum = selectedMonth && selectedMonth !== 'all' ? parseInt(selectedMonth) : 1;
            yearDateTime = `${yearNum}-${String(monthNum).padStart(2, '0')}-01T00:00:00`;
        }

        if (selectedMonth && selectedMonth !== 'all' && selectedYear && selectedYear !== 'all') {
            const yearNum = parseInt(selectedYear);
            const monthNum = parseInt(selectedMonth);
            monthDateTime = `${yearNum}-${String(monthNum).padStart(2, '0')}-01T00:00:00`;
        }

        const finalCustomerId = selectedClient && selectedClient !== 'all' ? selectedClient : '';
        const finalSearchQuery = searchQuery || '';

        dispatch(
            fetchTimesheetDeductions({
                fromDate: '',
                toDate: '',
                customerId: finalCustomerId,
                pageNumber: 1,
                tab: 2,
                searchQuery: finalSearchQuery,
                year: yearDateTime,
                month: monthDateTime,
            })
        );
    };

    const handleCustomerSelect = (customerId: number) => {
        setSelectedCustomerForDeduction(customerId);
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'invoiced':
                return <DeductionINvoiceSection 
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                    selectedClient={selectedClient}
                    searchQuery={searchQuery}
                />;
            case 'not-invoiced':
                return <NotInvoicedSection 
                    onEditDeduction={handleOpenEditModal}
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                    customerId={selectedClient}
                    searchQuery={searchQuery}
                />;
            default:
                return <DeductionINvoiceSection 
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                    selectedClient={selectedClient}
                    searchQuery={searchQuery}
                />;
        }
    };

    return (
        <div className="dashboard-page">
            <div className="dashboard-layout">
                <Sidebar
                    activeItemId='deduction-invoice'
                    footer={null}
                    onClose={() => setIsSidebarOpen(false)}
                    className={`dashboard-sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}
                />
                <div className="dashboard-main-content">
                    <Header
                        setIsSidebarOpen={setIsSidebarOpen}
                    />
                    <div className='px-3'>
                        <h3 className="dashboard-page__title">{t('DEDUCTIONS_LABEL')}</h3>
                        
                        <p className="deduction-page__description">
                            {t('DEDUCTION_DESCRIPTION') || 'Here are all the deductions made via invoices created through Invozio.'}
                        </p>

                        <div className="deduction-page__filters-row">
                            <DeductionFilters
                                selectedYear={selectedYear}
                                selectedMonth={selectedMonth}
                                selectedClient={selectedClient}
                                searchQuery={searchQuery}
                                onYearChange={setSelectedYear}
                                onMonthChange={setSelectedMonth}
                                onClientChange={setSelectedClient}
                                onSearchChange={(e) => setSearchQuery(e.target.value)}
                                clients={customerOptions}
                                loading={customersLoading}
                            />
                            {activeTab === 'not-invoiced' && (
                                <div className="deduction-page__actions">
                                    <AddDeductionButton onClick={handleOpenModal} />
                                </div>
                            )}
                        </div>

                        <DeductionTabs 
                            tabs={tabs}
                            activeTab={activeTab}
                            onTabChange={setActiveTab}
                        />

                        <div className="deduction-page__content">
                            {renderTabContent()}
                        </div>
                    </div>

                </div>
            </div>
            
            {/* Add Deduction Modal */}
            <AddDeductionModal
                isOpen={showDeductionModal}
                onClose={handleCloseModal}
                onSubmit={handleModalSubmit}
                register={register}
                setValue={setValue}
                watch={watch}
                errors={errors}
                trigger={trigger}
                t={t}
                editingDeductionData={editingDeductionData}
                preferences={preferences}
                showClientSelector={true}
                customers={customerOptions}
                selectedCustomerId={selectedCustomerForDeduction}
                onCustomerChange={handleCustomerSelect}
            />
        </div>
    );
}

export default DeductionPage;
